<template>
  <div class="device-selection-section">
    <h5 v-if="!hideHeading" class="mb-3 fw-bold">
      <FontAwesomeIcon icon="server" class="me-2" />
      {{ $t("portfolio_instructions.select_devices") }}
    </h5>

    <!-- Device Selection Dropdown -->
    <div class="mb-3">
      <label class="form-label device-selection-section__label">
        <FontAwesomeIcon icon="bolt" class="me-1" />
        {{ $t("portfolio_instructions.add_devices") }}
      </label>
      <RegistryDeviceSelector
        :modelValue="portfolioInstructions.registry_device_ids"
        :registryDevices="registryDevices"
        :availableOptions="availableDeviceOptions"
        :deviceValidationErrors="deviceValidationMessages"
        :placeholder="deviceSearchPlaceholder"
        @update:modelValue="handleDeviceSelectionUpdate"
      />

      <!-- Validation Error -->
      <div v-if="v$.registry_device_ids.$error" class="text-danger small mt-2">
        <FontAwesomeIcon icon="exclamation-triangle" class="me-1" />
        <span v-for="error of v$.registry_device_ids.$errors" :key="error.$uid">
          {{ error.$message }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import {
  getDeviceName,
  getDeviceSearchPlaceholder,
} from "@/dashboard/production_strategy/overview/PPA/ppaDeviceUtils";
import {
  analyzeDeviceUsage,
  createPeriodsFromLimits,
  getAvailableDeviceOptions,
  hasValidationErrors,
  validateAllDevices,
} from "@/dashboard/production_strategy/overview/PPA/ppaDeviceValidationUtils";
import { determineAllocationMode } from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import RegistryDeviceSelector from "@/dashboard/production_strategy/overview/RegistryDeviceSelector.vue";
import type {
  PortfolioInstructions,
  PortfolioWithInstructions,
  RegistryDevice,
} from "@/types";
import { useVuelidate } from "@vuelidate/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  registryDevices: RegistryDevice[];
  existingPortfolios?: PortfolioWithInstructions[];
  currentPortfolioId?: number;
  hideHeading?: boolean;
}>();

const portfolioInstructions = defineModel<PortfolioInstructions>({
  required: true,
});

const { t } = useI18n();
const customValidators = useValidators(t);

// Validation rules
const rules = computed(() => ({
  registry_device_ids: {
    required: customValidators.selectionRequired,
  },
}));

const v$ = useVuelidate(
  rules,
  computed(() => ({
    registry_device_ids: portfolioInstructions.value.registry_device_ids,
  })),
  {
    $autoDirty: true,
  }
);

const allocationMode = computed(() => {
  return determineAllocationMode(
    portfolioInstructions.value.allocate_percentage,
    portfolioInstructions.value.limits
  );
});

// Current portfolio's periods for period-aware validation
const currentPeriods = computed(() => {
  return createPeriodsFromLimits(
    portfolioInstructions.value.limits ?? [],
    "current"
  );
});

// Analyze device usage across all PPA portfolios
const deviceUsageAnalysis = computed(() => {
  return analyzeDeviceUsage(
    props.existingPortfolios,
    props.currentPortfolioId,
    portfolioInstructions.value?.registry_device_ids ?? []
  );
});

// Per-device validation for allocation conflicts
const deviceValidation = computed(() => {
  return validateAllDevices(
    portfolioInstructions.value.registry_device_ids ?? [],
    allocationMode.value,
    portfolioInstructions.value.allocate_percentage,
    deviceUsageAnalysis.value,
    t,
    getDeviceNameLocal,
    currentPeriods.value
  );
});

// Overall validation status for devices
const hasDeviceValidationErrors = computed(() => {
  return hasValidationErrors(deviceValidation.value);
});

const deviceValidationMessages = computed<Record<string, string>>(() =>
  Object.fromEntries(
    Object.entries(deviceValidation.value)
      .filter(([, validation]) => validation?.errorMessage)
      .map(([deviceId, validation]) => [deviceId, validation.errorMessage!])
  )
);

// All available device options for MultiSelectComponent (don't filter out selected ones)
const availableDeviceOptions = computed(() => {
  return getAvailableDeviceOptions(
    props.registryDevices ?? [],
    allocationMode.value,
    deviceUsageAnalysis.value,
    currentPeriods.value
  );
});

const registeredDeviceIds = computed(() =>
  (props.registryDevices ?? [])
    .filter((device) => device.registry_status === "registered")
    .map((device) => device.id.toString())
);

const hasUserInteracted = ref(false);

const maybeAutoSelectDevices = () => {
  if (hasUserInteracted.value) {
    return;
  }
  if ((portfolioInstructions.value.registry_device_ids ?? []).length > 0) {
    return;
  }
  if (registeredDeviceIds.value.length === 0) {
    return;
  }
  hasUserInteracted.value = true;
  portfolioInstructions.value = {
    ...portfolioInstructions.value,
    registry_device_ids: [...registeredDeviceIds.value],
  };
};

watch(
  () => [
    portfolioInstructions.value.registry_device_ids,
    registeredDeviceIds.value,
  ],
  () => maybeAutoSelectDevices(),
  { immediate: true, deep: true }
);

// Enhanced placeholder text that reflects current state
const deviceSearchPlaceholder = computed(() => {
  const availableDevicesCount = availableDeviceOptions.value.length;
  const totalDevicesCount = props.registryDevices?.length ?? 0;
  const usedDevicesCount = Object.keys(deviceUsageAnalysis.value).length;

  return getDeviceSearchPlaceholder(
    availableDevicesCount,
    totalDevicesCount,
    usedDevicesCount,
    t
  );
});

// Use imported utility function for device name
const getDeviceNameLocal = (deviceId: string) => {
  return getDeviceName(deviceId, props.registryDevices ?? []);
};

const handleDeviceSelectionUpdate = (deviceIds: string[]) => {
  hasUserInteracted.value = true;
  portfolioInstructions.value = {
    ...portfolioInstructions.value,
    registry_device_ids: deviceIds ?? [],
  };
  v$.value.registry_device_ids.$touch();
};

// Expose validation methods for parent component
defineExpose({
  isValid: () => {
    return !v$.value.$invalid && !hasDeviceValidationErrors.value;
  },
  touchAndValidate: () => {
    v$.value.$touch();
    return !v$.value.$invalid && !hasDeviceValidationErrors.value;
  },
});
</script>
